/**
 * ApplyDialog — shared application form dialog used by Opportunities and DemandDetail.
 */
import { useState } from 'react'
import type { Demand } from '../types/demand'
import { OpportunitySummary } from './OpportunitySummary'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Textarea,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
})

export interface ApplyDialogProps {
  demand?: Demand
  demandTitle: string
  open: boolean
  onClose: () => void
  onSubmit: (input: {
    cover_letter: string
    proposed_focus?: string
    proposed_hours_per_week?: number
    proposed_duration?: string
  }) => void | Promise<void>
  submitting: boolean
}

export function ApplyDialog({
  demand,
  demandTitle,
  open,
  onClose,
  onSubmit,
  submitting,
}: ApplyDialogProps) {
  const styles = useStyles()
  const [coverLetter, setCoverLetter] = useState('')
  const [proposedFocus, setProposedFocus] = useState('')
  const [proposedHours, setProposedHours] = useState('')
  const [proposedDuration, setProposedDuration] = useState('')

  // Mirror backend schema (schemas/application.py:cover_letter min_length=20).
  // Keeping these in sync prevents the 422 → "Could not submit" round-trip.
  const COVER_LETTER_MIN = 20
  const coverLetterChars = coverLetter.trim().length
  const coverLetterShort = coverLetterChars < COVER_LETTER_MIN
  const canSubmit = !coverLetterShort && !submitting

  // Escape and a backdrop click used to close unconditionally, and
  // `Opportunities.tsx` unmounts the dialog on close — taking these four
  // drafts with it. A written cover letter is the most expensive thing a
  // talent types in this app, and re-opening gave them a blank form (#1247).
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const dirty = Boolean(
    coverLetter.trim() || proposedFocus.trim() || proposedHours.trim() || proposedDuration.trim(),
  )

  function attemptClose() {
    if (dirty) setConfirmDiscard(true)
    else onClose()
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    const hours = proposedHours.trim() ? Number(proposedHours) : undefined
    onSubmit({
      cover_letter: coverLetter.trim(),
      proposed_focus: proposedFocus.trim() || undefined,
      proposed_hours_per_week: Number.isFinite(hours) ? (hours as number) : undefined,
      proposed_duration: proposedDuration.trim() || undefined,
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) attemptClose()
      }}
      modalType="modal"
    >
      <DialogSurface aria-describedby={undefined}>
        <DialogBody>
          <DialogTitle>Apply to {demandTitle}</DialogTitle>
          <DialogContent>
            {demand && <OpportunitySummary demand={demand} />}
            {/* Inline rather than a second Dialog. Two modals open at once
                leaves the form aria-hidden after the alert closes, and the
                nested form breaks Fluent's motion wrapper outright — but the
                better reason is that the text stays on screen while the
                question is being answered. */}
            {confirmDiscard && (
              <MessageBar intent="warning" data-testid="apply-discard">
                <MessageBarBody>
                  You haven&apos;t submitted this yet. Closing now loses what you typed.
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    appearance="primary"
                    size="small"
                    onClick={() => setConfirmDiscard(false)}
                    data-testid="apply-discard-cancel"
                  >
                    Keep editing
                  </Button>
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={() => {
                      setConfirmDiscard(false)
                      onClose()
                    }}
                    data-testid="apply-discard-confirm"
                  >
                    Discard
                  </Button>
                </MessageBarActions>
              </MessageBar>
            )}
            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmit()
              }}
            >
              <Field
                label={`Cover letter (min. ${COVER_LETTER_MIN} characters)`}
                required
                hint={
                  coverLetterShort
                    ? `${coverLetterChars}/${COVER_LETTER_MIN} characters — need ${COVER_LETTER_MIN - coverLetterChars} more to submit.`
                    : `✓ ${coverLetterChars} characters`
                }
                validationState={coverLetterShort && coverLetterChars > 0 ? 'warning' : coverLetterShort ? 'none' : 'success'}
              >
                <Textarea
                  value={coverLetter}
                  onChange={(_, data) => setCoverLetter(data.value)}
                  rows={5}
                  placeholder="I'd like to contribute because..."
                />
              </Field>
              <Field
                label="Proposed focus"
                hint="What aspect of this demand would you take on?"
              >
                <Textarea
                  value={proposedFocus}
                  onChange={(_, data) => setProposedFocus(data.value)}
                  rows={3}
                  placeholder="e.g. lead the data pipeline design"
                />
              </Field>
              <div className={styles.twoCol}>
                <Field label="Proposed hours / week">
                  <Input
                    type="number"
                    min={0}
                    value={proposedHours}
                    onChange={(_, data) => setProposedHours(data.value)}
                    placeholder="e.g. 10"
                  />
                </Field>
                <Field label="Proposed duration">
                  <Input
                    value={proposedDuration}
                    onChange={(_, data) => setProposedDuration(data.value)}
                    placeholder="e.g. 3 months"
                  />
                </Field>
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={attemptClose} disabled={submitting}>
                Cancel
              </Button>
            </DialogTrigger>
            <Tooltip
              content={coverLetterShort ? `Cover letter needs ${COVER_LETTER_MIN - coverLetterChars} more characters` : ''}
              relationship="description"
              visible={coverLetterShort && coverLetterChars > 0 ? undefined : false}
            >
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? 'Submitting...' : 'Submit application'}
              </Button>
            </Tooltip>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
